import React, { Fragment, useState } from 'react';
import { useAsync } from 'react-async';
import { useHistory } from 'react-router';
import { createUser } from '../../../api/services/userService';
import { getAvailableRoles } from '../../../api/services/roleService';
import { ResponseStatus } from '../../../components/notifications';
import { FieldInput } from '../../../components/fieldInput';
import Layout from '../../../components/layout';
import SubmitButton from '../../../components/submitButton';
import cx from 'classnames';
import { RoleIdLookup } from '../../../common/constants';

type Rule = {
  text: string;
  validation: () => boolean;
};

const CreateUsers = () => {
  const history = useHistory();

  // State
  const [firstName, setFirstName] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [middleInitial, setMiddleInitial] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [roles, setRoles] = useState<(number | undefined)[]>([]);

  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');

  const roleData = useAsync({
    promiseFn: getAvailableRoles,
  });

  const saveUser = async () => {
    return await createUser({
      firstName: firstName,
      middleInitial: middleInitial,
      lastName: lastName,
      emailAddress: email,
      isDisabled: false,
      password: password,
      roleIds: roles,
    });
  };

  const save = useAsync({
    deferFn: saveUser,
    onResolve: (res) => {
      if (roles.some(r => r == RoleIdLookup.Therapist)) {
        history.push('/admin/therapists/' + res.data, {
          message: `"${firstName + ' ' + lastName}" saved successfully!`,
        });
      } else {
        history.push('/admin/users', {
          message: `"${firstName + ' ' + lastName}" created successfully!`,
        });
      }
    },
  });

  // Handlers
  const handleRoleChange = (e: React.FormEvent<HTMLInputElement>) => {
    if (e.currentTarget.checked) {
      setRoles([...roles, parseInt(e.currentTarget.value)]);
    } else {
      setRoles([...roles.filter((r) => r !== parseInt(e.currentTarget.value))]);
    }
  };

  const handleSubmit = () => {
    save.run();
  };

  const rules: Rule[] = [
    {
      text: 'Between 8 and 20 characters',
      validation: () => {
        return password.length >= 8 && password.length <= 20;
      },
    },
    {
      text: 'Has uppercase character',
      validation: () => {
        return /[A-Z]/.test(password);
      },
    },
    {
      text: 'Has lowercase character',
      validation: () => {
        return /[a-z]/.test(password);
      },
    },
    {
      text: 'Has a digit',
      validation: () => {
        return /[0-9]/.test(password);
      },
    },
    {
      text: 'Has special character',
      validation: () => {
        //eslint-disable-next-line
        return /[\s~`!@#$%\^&*+=\-\[\]\\';,/{}|\\":<>\?()\._]/g.test(password);
      },
    },
    {
      text: 'Password & Confirm Password Match',
      validation: () => {
        return (
          password === confirmPw && password.length > 0 && confirmPw.length > 0
        );
      },
    },
  ];

  // Validation Rules
  const isValid =
    !rules.some((r) => r.validation() === false) && password.length > 0;

  return (
    <Layout>
      <Fragment>
        <ResponseStatus state={save} />
        <div className='grid grid-cols-6 gap-6 mb-6'>
          <div className='col-span-6 sm:col-span-6'>
            <FieldInput
              name='firstName'
              label='First Name'
              minLength={1}
              onChange={(e) => setFirstName(e.target.value)}
              value={firstName}
              autoComplete={'none'}
              lpIgnore={'true'}
            />
          </div>
          <div className='col-span-6 sm:col-span-6'>
            <FieldInput
              name='lastName'
              label='Last Name'
              minLength={1}
              onChange={(e) => setLastName(e.target.value)}
              value={lastName}
              autoComplete={'none'}
              lpIgnore={'true'}
            />
          </div>
          <div className='col-span-6 sm:col-span-6'>
            <FieldInput
              name='emailAddress'
              label='Email Address'
              minLength={1}
              onChange={(e) => setEmail(e.target.value)}
              value={email}
              autoComplete={'new-email-address'}
              lpIgnore={'true'}
            />
          </div>

          <hr className='col-span-6 sm:col-span-6' />
          <div className='col-span-6 sm:col-span-6'>
            <label>
              Roles<span className='ml-1 text-red-500'>*</span>
            </label>
            <p className='mb-3 text-sm text-gray-500'>
              Select all applicable entry types.
            </p>
            <div className='px-4 py-3 mt-1 bg-white rounded-lg shadow'>
              {roleData.data?.data.roles.map((et, i) => {
                const etid = `role_${i}`;

                return (
                  <div key={etid} className='flex items-start py-1'>
                    <div className='flex items-center h-5'>
                      <input
                        id={etid}
                        value={et.roleId}
                        checked={roles.includes(et.roleId)}
                        onChange={handleRoleChange}
                        name='entry-type'
                        type='checkbox'
                      />
                    </div>
                    <div className='ml-3 text-sm'>
                      <label htmlFor={etid}>{et.name}</label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className='col-span-6 sm:col-span-6'>
            <FieldInput
              name='password'
              label='Password'
              onChange={(e) => setPassword(e.target.value)}
              value={password}
              minLength={1}
              type='password'
              autoComplete={'new-password'}
              lpIgnore={'true'}
            />
          </div>
          <div className='col-span-6 sm:col-span-6'>
            <FieldInput
              label='Confirm Password'
              name='confirm-password'
              value={confirmPw}
              type='password'
              minLength={1}
              onChange={(e) => setConfirmPw(e.target.value)}
              autoComplete={'none'}
              lpIgnore={'true'}
            />
          </div>
          {!isValid && password.length > 0 && (
            <div className='col-span-6 sm:col-span-6'>
              <ol>
                {rules.map((r) => (
                  <li key={r.text} className='flex items-center mb-1'>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className={cx(
                        'h-6 w-6 mr-1',
                        r.validation() === true
                          ? 'text-ptsgreen-normal'
                          : 'text-gray-400'
                      )}
                      viewBox='0 0 20 20'
                      fill='currentColor'
                    >
                      <path
                        fillRule='evenodd'
                        d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                        clipRule='evenodd'
                      />
                    </svg>
                    <span className='text-sm'>{r.text}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
        <hr className='py-4' />
        <div className='text-right'>
          <SubmitButton
            text='Save Changes'
            disabled={email.length === 0 || roles.length === 0 || !isValid}
            spinning={save.isPending}
            onClick={handleSubmit}
          />
        </div>
      </Fragment>
    </Layout>
  );
};

export default CreateUsers;
